import { Alert, Box, Grid, useTheme } from '@mui/material';
import UPButton from 'components/button';
import { UPDropdown } from 'components/dropdown';
import { toNumber } from 'lodash';
import { CloseAlertIcon } from 'modules/companies/styles';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getParitairComiteSelectItemsAction,
  getParitairComiteSelectItemsByCompanyAction,
} from 'store/actions/paritair-comite-actions';
import {
  getOvertimeLimitsAction,
  getWageCalculationAction,
  getWorkhourSettingsAction,
  resetWageCalculationAction,
  setUpdateOvertimeLimitsErrorAction,
  setUpdateWorkhourSettingsErrorAction,
  updateOvertimeLimitsAction,
  updateWageCalculationAction,
  updateWorkhourSettingsAction,
  WageCalculationSaveResult,
} from 'store/actions/wage-calculation-actions';
import {
  paritairComiteByCompanySelectItemsSelector,
  paritairComiteSelectItemsSelector,
} from 'store/selectors/paritair-comite-selectors';
import {
  overtimeLimitsSelector,
  updateOvertimeLimitsErrorMessageSelector,
  updateOvertimeLimitsSuccessSelector,
  wageCalculationSelector,
  workhourSettingsSelector,
} from 'store/selectors/wage-calculation-selectors';
import {
  COMPANY_INFORMATION__COMPANY_UPDATED_SUCCESSFULLY,
  COMPANY_PAYROLL__WORKHOURS_SETTINGS,
  GENERAL__SELECT,
  MENU__PARITAIR_COMITE,
  WAGE_COMPONENTS__HOLIDAY,
  WAGE_COMPONENTS__NIGHT_SHIFT,
  WAGE_COMPONENTS__OVERTIME,
} from 'translations/constants';
import {
  INightShift,
  IOvertimeLimits,
  IServiceCode,
  IWageCalculationComponent,
  IWorkhourSettings,
} from 'types/wage-calculation';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import { formatTotalHours, parseHours, validateTotalHours } from './helpers';
import NightShiftSchema from './NightShiftSchema';
import OvertimeLimits from './OvertimeLimits';
import ServiceCodesTable from './ServiceCodesTable';
import {
  ContainerTitle,
  NightShiftIntervalContainer,
  OvertimeLimitsContainer,
  ServiceCodesButtonsContainer,
  ServiceCodesContainer,
  WorkhourSettingsContainer,
} from './styles';
import { ServiceCodesAction, ServiceCodesProps } from './types';
import WorkhourSettings from './WorkhourSettings';

const START_TIME = '22:00';
const END_TIME = '06:00';

const NightshiftInitial: INightShift = {
  id: 0,
  startTime: START_TIME,
  endTime: END_TIME,
  code: 0,
};

const ServiceCodeInitial: IServiceCode = {
  id: 0,
  outcome: '',
  code: 0,
  description: '',
};

const ComponentInitial: IWageCalculationComponent = {
  workHoursWeekDay: ServiceCodeInitial,
  workHoursSaturday: ServiceCodeInitial,
  workHoursSunday: ServiceCodeInitial,
  overtimeWeekDay: ServiceCodeInitial,
  overtimeSaturday: ServiceCodeInitial,
  overtimeSunday: ServiceCodeInitial,
};

const ServiceCodes = (props: ServiceCodesProps) => {
  const { companyId } = props;

  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const paritairComiteSelectItems = useAppSelector(paritairComiteSelectItemsSelector);
  const paritairComiteSelectItemsByCompany = useAppSelector(paritairComiteByCompanySelectItemsSelector);
  const wageCalculationFromRedux = useAppSelector(wageCalculationSelector);
  const overtimeLimitsFromRedux = useAppSelector(overtimeLimitsSelector);
  const workhourSettingsFromRedux = useAppSelector(workhourSettingsSelector);
  const updateOvertimeLimitsErrorMessage = useAppSelector(updateOvertimeLimitsErrorMessageSelector);
  const updateOvertimeLimitsSuccess = useAppSelector(updateOvertimeLimitsSuccessSelector);

  const [nightShift, setNightShift] = useState<INightShift>(NightshiftInitial);
  const [overtime, setOvertime] = useState<IWageCalculationComponent>(ComponentInitial);
  const [holiday, setHoliday] = useState<IWageCalculationComponent>(ComponentInitial);
  const [overtimeLimits, setOvertimeLimits] = useState<IOvertimeLimits>(null);
  const [workhourSettings, setWorkhourSettings] = useState<IWorkhourSettings>(null);
  const [selectedParitairComite, setSelectedParitairComite] = useState<string>('');
  const [areOvertimeLimitsUpdated, setAreOvertimeLimitsUpdated] = useState<boolean>(false);
  const [areWorkhourSettingsUpdated, setAreWorkhourSettingsUpdated] = useState<boolean>(false);
  const [areServiceCodesUpdated, setAreServiceCodesUpdated] = useState<boolean>(false);
  const [validValues, setValidValues] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveResult, setSaveResult] = useState<WageCalculationSaveResult | undefined>(undefined);

  const shouldUpdate = useMemo(() => {
    return areOvertimeLimitsUpdated || areServiceCodesUpdated || areWorkhourSettingsUpdated;
  }, [areOvertimeLimitsUpdated, areServiceCodesUpdated, areWorkhourSettingsUpdated]);

  // validValues only reflects the last edited field, so total hours is checked again here
  const areTotalHoursValid = useMemo(
    () => !areWorkhourSettingsUpdated || validateTotalHours(workhourSettings?.totalHours, false).isValid,
    [areWorkhourSettingsUpdated, workhourSettings],
  );

  const paritairComiteItems = useMemo(() => {
    if (companyId) {
      return paritairComiteSelectItemsByCompany;
    }
    return paritairComiteSelectItems;
  }, [paritairComiteSelectItems, paritairComiteSelectItemsByCompany, companyId]);

  const onAction = (action: ServiceCodesAction) => {
    setAreServiceCodesUpdated(false);
    if (action === ServiceCodesAction.RESET) {
      dispatch(
        resetWageCalculationAction({
          paritairComiteId: toNumber(selectedParitairComite),
          companyId: companyId ?? 0,
          wageCalculation: {
            nightShiftRule: nightShift,
            overtimeRules: overtime,
            holidayRules: holiday,
          },
        }),
      );
      return Promise.resolve<WageCalculationSaveResult>(null);
    }
    return dispatch(
      updateWageCalculationAction({
        paritairComiteId: toNumber(selectedParitairComite),
        companyId: companyId ?? 0,
        wageCalculation: {
          nightShiftRule: nightShift,
          overtimeRules: overtime,
          holidayRules: holiday,
        },
      }),
    ).unwrap();
  };

  const onClickUpdate = async () => {
    const saves: Promise<WageCalculationSaveResult>[] = [];
    setIsSaving(true);
    setSaveResult(undefined);
    if (areOvertimeLimitsUpdated) {
      saves.push(
        dispatch(
          updateOvertimeLimitsAction({
            companyId,
            paritairComiteId: toNumber(selectedParitairComite),
            overtimeLimits,
          }),
        ).unwrap(),
      );
      setAreOvertimeLimitsUpdated(false);
    }
    if (areWorkhourSettingsUpdated) {
      saves.push(
        dispatch(
          updateWorkhourSettingsAction({
            companyId,
            paritairComiteId: toNumber(selectedParitairComite),
            workhourSettings: { ...workhourSettings, totalHours: parseHours(workhourSettings.totalHours) },
          }),
        ).unwrap(),
      );
      setAreWorkhourSettingsUpdated(false);
    }
    if (areServiceCodesUpdated) {
      saves.push(onAction(ServiceCodesAction.UPDATE));
      setAreServiceCodesUpdated(false);
    }
    const results = await Promise.all(saves);
    const errors = results.filter(result => result !== null);
    // one alert for the whole Update; an error that is already shown elsewhere ('') only suppresses the success alert
    setSaveResult(errors.length === 0 ? null : errors.find(error => !!error) ?? '');
    setIsSaving(false);
  };

  useEffect(() => {
    if (paritairComiteItems.length > 0) {
      setSelectedParitairComite(paritairComiteItems[0].value);
    }
  }, [paritairComiteItems]);

  useEffect(() => {
    if (companyId) {
      dispatch(getParitairComiteSelectItemsByCompanyAction(companyId));
      dispatch(setUpdateOvertimeLimitsErrorAction(''));
      dispatch(setUpdateWorkhourSettingsErrorAction(''));
      return;
    }
    dispatch(getParitairComiteSelectItemsAction());
  }, [dispatch, companyId]);

  useEffect(() => {
    if (selectedParitairComite) {
      dispatch(
        getWageCalculationAction({ paritairComiteId: toNumber(selectedParitairComite), companyId: companyId ?? 0 }),
      );
      if (companyId) {
        dispatch(getOvertimeLimitsAction({ companyId, paritairComiteId: toNumber(selectedParitairComite) }));
        dispatch(getWorkhourSettingsAction({ companyId, paritairComiteId: toNumber(selectedParitairComite) }));
      }
    }
  }, [dispatch, selectedParitairComite, companyId]);

  useEffect(() => {
    if (wageCalculationFromRedux) {
      setNightShift(wageCalculationFromRedux.nightShiftRule);
      setOvertime(wageCalculationFromRedux.overtimeRules);
      setHoliday(wageCalculationFromRedux.holidayRules);
    }
    if (overtimeLimitsFromRedux) {
      setOvertimeLimits(overtimeLimitsFromRedux);
    }
    if (workhourSettingsFromRedux) {
      setWorkhourSettings({
        ...workhourSettingsFromRedux,
        totalHours: formatTotalHours(workhourSettingsFromRedux.totalHours) ?? '',
      });
    }
  }, [overtimeLimitsFromRedux, wageCalculationFromRedux, workhourSettingsFromRedux]);

  return (
    <Grid container marginTop={theme.spacing(1.25)}>
      <Box marginBottom={theme.spacing(2)} width={1}>
        <Box display="flex" alignItems="flex-end">
          <Box width="200px">
            <UPDropdown
              label={t(MENU__PARITAIR_COMITE)}
              placeholder={t(GENERAL__SELECT)}
              items={paritairComiteItems}
              value={selectedParitairComite}
              onChange={event => {
                const { value } = event.target;
                setSelectedParitairComite(value);
                setAreOvertimeLimitsUpdated(false);
                setAreServiceCodesUpdated(false);
                setSaveResult(undefined);
              }}
            />
          </Box>
          <ServiceCodesButtonsContainer>
            {!!companyId && <UPButton text="Reset" onClick={() => onAction(ServiceCodesAction.RESET)} />}
            {shouldUpdate && (
              <UPButton
                text="Update"
                onClick={onClickUpdate}
                disabled={!validValues || !areTotalHoursValid || isSaving}
              />
            )}
          </ServiceCodesButtonsContainer>
          {!updateOvertimeLimitsSuccess && (
            <Box sx={{ ml: theme.spacing(1), color: theme.palette.error.main, display: 'flex', alignItems: 'center' }}>
              {updateOvertimeLimitsErrorMessage}
            </Box>
          )}
        </Box>
        {!!companyId && saveResult === null && (
          <Alert
            severity="success"
            icon={false}
            variant="filled"
            sx={{ width: '800px', mt: theme.spacing(2) }}
            action={
              <CloseAlertIcon
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setSaveResult(undefined);
                }}
              >
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(COMPANY_INFORMATION__COMPANY_UPDATED_SUCCESSFULLY)}
          </Alert>
        )}
        {!!companyId && !!saveResult && (
          <Alert severity="error" icon={false} variant="filled" sx={{ width: '800px', mt: theme.spacing(2) }}>
            {t(saveResult)}
          </Alert>
        )}
      </Box>
      {!!companyId && (
        <>
          <Grid item xs={6} marginBottom="20px" paddingRight={theme.spacing(1)}>
            <ServiceCodesContainer>
              <ContainerTitle>{t(COMPANY_PAYROLL__WORKHOURS_SETTINGS)}</ContainerTitle>
              <WorkhourSettingsContainer>
                <WorkhourSettings
                  workhourSettings={workhourSettings}
                  setWorkhourSettings={setWorkhourSettings}
                  setShouldUpdate={setAreWorkhourSettingsUpdated}
                  setValidValues={setValidValues}
                />
              </WorkhourSettingsContainer>
            </ServiceCodesContainer>
          </Grid>
          <Grid item xs={6} marginBottom="20px" paddingLeft={theme.spacing(1)}>
            <ServiceCodesContainer>
              <ContainerTitle>Overtime limits</ContainerTitle>
              <OvertimeLimitsContainer>
                <OvertimeLimits
                  overtimeLimits={overtimeLimits}
                  setOvertimeLimits={setOvertimeLimits}
                  setShouldUpdate={setAreOvertimeLimitsUpdated}
                  setValidValues={setValidValues}
                />
              </OvertimeLimitsContainer>
            </ServiceCodesContainer>
          </Grid>
        </>
      )}

      <ServiceCodesContainer xs={12} marginBottom="20px">
        <ContainerTitle>{t(WAGE_COMPONENTS__NIGHT_SHIFT)}</ContainerTitle>
        <NightShiftIntervalContainer>
          <NightShiftSchema
            nightShiftRule={nightShift}
            setNightShiftRule={setNightShift}
            setShouldUpdate={setAreServiceCodesUpdated}
            setValidValues={setValidValues}
          />
        </NightShiftIntervalContainer>
      </ServiceCodesContainer>
      <Grid item xs={6} paddingRight={theme.spacing(1)}>
        <ServiceCodesContainer>
          <ContainerTitle>{t(WAGE_COMPONENTS__OVERTIME)}</ContainerTitle>
          <ServiceCodesTable
            wageCalculationComponent={overtime}
            setWageCalculationComponent={setOvertime}
            setShouldUpdate={setAreServiceCodesUpdated}
            setValidValues={setValidValues}
          />
        </ServiceCodesContainer>
      </Grid>
      <Grid item xs={6} paddingLeft={theme.spacing(1)}>
        <ServiceCodesContainer>
          <ContainerTitle>{t(WAGE_COMPONENTS__HOLIDAY)}</ContainerTitle>
          <ServiceCodesTable
            wageCalculationComponent={holiday}
            setWageCalculationComponent={setHoliday}
            setShouldUpdate={setAreServiceCodesUpdated}
            setValidValues={setValidValues}
          />
        </ServiceCodesContainer>
      </Grid>
    </Grid>
  );
};

export default ServiceCodes;
